import React, { useState, useEffect, useMemo } from 'react';
import { stockApi, productsApi, depotsApi, locationsApi, stockReportApi } from '../lib/api';
import SearchableSelect from '../components/SearchableSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { 
  Plus, ArrowDownRight, ArrowUpRight, ArrowLeftRight, 
  Package, Warehouse, Clock, Filter, Download, FileText,
  ArrowUp, ArrowDown, ArrowUpDown, Search
} from 'lucide-react';

const StockPage = () => {
  const [stockItems, setStockItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stock');
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  const [depotFilter, setDepotFilter] = useState('all');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [stockSearch, setStockSearch] = useState('');
  const [stockSortBy, setStockSortBy] = useState('reference');
  const [stockSortOrder, setStockSortOrder] = useState('asc');

  // Forecast state
  const [forecastData, setForecastData] = useState(null);
  const [forecastSearch, setForecastSearch] = useState('');
  const [forecastFilter, setForecastFilter] = useState('all'); // all, in_depot_only, on_order_only
  const [forecastSortBy, setForecastSortBy] = useState('reference');
  const [forecastSortOrder, setForecastSortOrder] = useState('asc');

  const [movementForm, setMovementForm] = useState({
    product_id: '',
    depot_id: '',
    location_id: '',
    movement_type: 'entry',
    quantity: '',
    reason: '',
  });

  useEffect(() => {
    fetchData();
  }, [depotFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = depotFilter !== 'all' ? { depot_id: depotFilter } : {};
      
      const [stockRes, movementsRes, productsRes, depotsRes] = await Promise.all([
        stockApi.getAll(params),
        stockApi.getMovements({ ...params, limit: 100 }),
        productsApi.getAll({ limit: 500 }),
        depotsApi.getAll(),
      ]);

      setStockItems(stockRes.data);
      setMovements(movementsRes.data.items || []);
      setProducts(productsRes.data.items || []);
      setDepots(depotsRes.data);

      // Load forecast data
      try {
        const forecastRes = await stockApi.getForecast();
        setForecastData(forecastRes.data);
      } catch { setForecastData(null); }
    } catch (error) {
      console.error('Error fetching stock data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMovement = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...movementForm,
        quantity: parseInt(movementForm.quantity),
      };
      
      // Remove empty fields
      Object.keys(data).forEach(key => {
        if (data[key] === '') delete data[key];
      });

      await stockApi.createMovement(data);
      toast.success('Mouvement enregistré');
      setShowMovementDialog(false);
      fetchData();
      setMovementForm({
        product_id: '',
        depot_id: '',
        location_id: '',
        movement_type: 'entry',
        quantity: '',
        reason: '',
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'enregistrement'));
    }
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'entry': return <ArrowDownRight className="w-4 h-4 text-green-400" />;
      case 'exit': return <ArrowUpRight className="w-4 h-4 text-red-400" />;
      case 'transfer': return <ArrowLeftRight className="w-4 h-4 text-blue-400" />;
      case 'return': return <ArrowDownRight className="w-4 h-4 text-purple-400" />;
      case 'adjustment': return <Package className="w-4 h-4 text-amber-400" />;
      case 'consumption': return <ArrowUpRight className="w-4 h-4 text-orange-400" />;
      default: return <Package className="w-4 h-4 text-zinc-400" />;
    }
  };

  const handleExportStockPdf = async () => {
    setExportingPdf(true);
    try {
      const payload = {};
      if (depotFilter && depotFilter !== 'all') payload.depot_id = depotFilter;
      const res = await stockReportApi.generate(payload);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etat_stock.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('État du stock exporté en PDF');
    } catch {
      toast.error('Erreur lors de l\'export');
    }
    setExportingPdf(false);
  };

  const getMovementLabel = (type) => {
    const labels = {
      entry: 'Entrée',
      exit: 'Sortie',
      transfer: 'Transfert',
      return: 'Retour',
      adjustment: 'Ajustement',
      consumption: 'Consommation'
    };
    return labels[type] || type;
  };

  const getMovementBadgeClass = (type) => {
    const classes = {
      entry: 'bg-green-500/20 text-green-400 border-green-500/30',
      exit: 'bg-red-500/20 text-red-400 border-red-500/30',
      transfer: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      return: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      adjustment: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      consumption: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    };
    return classes[type] || '';
  };

  const filteredAndSortedStock = useMemo(() => {
    let items = [...stockItems];
    if (stockSearch) {
      const q = stockSearch.toLowerCase();
      items = items.filter(s => 
        (s.product?.reference || '').toLowerCase().includes(q) ||
        (s.product?.designation || '').toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => {
      let valA, valB;
      switch (stockSortBy) {
        case 'reference': valA = a.product?.reference || ''; valB = b.product?.reference || ''; break;
        case 'designation': valA = a.product?.designation || ''; valB = b.product?.designation || ''; break;
        case 'category': valA = a.product?.category || ''; valB = b.product?.category || ''; break;
        case 'available_quantity': return stockSortOrder === 'asc' ? (a.available_quantity||0)-(b.available_quantity||0) : (b.available_quantity||0)-(a.available_quantity||0);
        case 'nec': { const nA = (a.available_quantity||0)*(a.product?.nec||0); const nB = (b.available_quantity||0)*(b.product?.nec||0); return stockSortOrder === 'asc' ? nA-nB : nB-nA; }
        default: valA = a.product?.reference || ''; valB = b.product?.reference || '';
      }
      const cmp = valA.toString().localeCompare(valB.toString(), 'fr');
      return stockSortOrder === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [stockItems, stockSearch, stockSortBy, stockSortOrder]);

  const toggleStockSort = (field) => {
    if (stockSortBy === field) setStockSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setStockSortBy(field); setStockSortOrder('asc'); }
  };

  const StockSortIcon = ({ field }) => {
    if (stockSortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
    return stockSortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 text-amber-400" /> : <ArrowDown className="w-3 h-3 ml-1 text-amber-400" />;
  };

  return (
    <div className="space-y-6" data-testid="stock-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Gestion des Stocks</h1>
          <p className="text-zinc-400 text-sm mt-1">Suivi des quantités et mouvements</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={depotFilter} onValueChange={setDepotFilter}>
            <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white">
              <Warehouse className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Tous les dépôts" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-white">Tous les dépôts</SelectItem>
              {depots.map(depot => (
                <SelectItem key={depot.id} value={depot.id} className="text-white">{depot.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="border-zinc-700 text-zinc-300" onClick={handleExportStockPdf}
            disabled={exportingPdf} data-testid="export-stock-pdf">
            <FileText className="w-4 h-4 mr-2" /> {exportingPdf ? 'Export...' : 'Export PDF'}
          </Button>
          <Dialog open={showMovementDialog} onOpenChange={setShowMovementDialog}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-movement-button">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Mouvement
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Nouveau Mouvement de Stock</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateMovement} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Type de mouvement *</Label>
                  <Select 
                    value={movementForm.movement_type} 
                    onValueChange={(v) => setMovementForm({...movementForm, movement_type: v})}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="entry" className="text-white">
                        <div className="flex items-center gap-2">
                          <ArrowDownRight className="w-4 h-4 text-green-400" /> Entrée
                        </div>
                      </SelectItem>
                      <SelectItem value="exit" className="text-white">
                        <div className="flex items-center gap-2">
                          <ArrowUpRight className="w-4 h-4 text-red-400" /> Sortie
                        </div>
                      </SelectItem>
                      <SelectItem value="transfer" className="text-white">
                        <div className="flex items-center gap-2">
                          <ArrowLeftRight className="w-4 h-4 text-blue-400" /> Transfert
                        </div>
                      </SelectItem>
                      <SelectItem value="adjustment" className="text-white">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-amber-400" /> Ajustement
                        </div>
                      </SelectItem>
                      <SelectItem value="return" className="text-white">
                        <div className="flex items-center gap-2">
                          <ArrowDownRight className="w-4 h-4 text-purple-400" /> Retour
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Produit *</Label>
                  <SearchableSelect
                    value={movementForm.product_id}
                    onValueChange={(v) => setMovementForm({...movementForm, product_id: v})}
                    options={products.map(p => ({ value: p.id, label: `${p.reference} — ${p.designation?.substring(0, 40) || ''}` }))}
                    placeholder="Rechercher un produit..."
                    searchPlaceholder="Tapez une référence ou désignation..."
                    data-testid="movement-product-select"
                    renderOption={(opt) => {
                      const parts = opt.label.split(' — ');
                      return <><span className="font-mono text-cyan-400 mr-2">{parts[0]}</span><span className="text-zinc-300">{parts[1] || ''}</span></>;
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Dépôt *</Label>
                  <SearchableSelect
                    value={movementForm.depot_id}
                    onValueChange={(v) => setMovementForm({...movementForm, depot_id: v})}
                    options={depots.map(d => ({ value: d.id, label: d.name }))}
                    placeholder="Sélectionner un dépôt..."
                    searchPlaceholder="Rechercher un dépôt..."
                    data-testid="movement-depot-select"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Quantité *</Label>
                  <Input
                    type="number"
                    value={movementForm.quantity}
                    onChange={(e) => setMovementForm({...movementForm, quantity: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="0"
                    required
                    min="1"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Motif</Label>
                  <Input
                    value={movementForm.reason}
                    onChange={(e) => setMovementForm({...movementForm, reason: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Raison du mouvement..."
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                  <Button type="button" variant="outline" onClick={() => setShowMovementDialog(false)} className="border-zinc-700">
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                    Enregistrer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="stock" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Package className="w-4 h-4 mr-2" /> État des Stocks
          </TabsTrigger>
          <TabsTrigger value="movements" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Clock className="w-4 h-4 mr-2" /> Historique Mouvements
          </TabsTrigger>
          <TabsTrigger value="forecast" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Warehouse className="w-4 h-4 mr-2" /> Previsionnel
            {forecastData?.total_on_order > 0 && (
              <Badge className="ml-1.5 text-[10px] bg-amber-500/20 text-amber-400 border-amber-500/30 px-1.5">
                +{forecastData.total_on_order}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Stock Tab */}
        <TabsContent value="stock" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input placeholder="Rechercher dans le stock..." value={stockSearch} onChange={(e) => setStockSearch(e.target.value)}
                    className="pl-10 bg-zinc-950 border-zinc-800 text-white" data-testid="stock-search" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">Tri :</span>
                  <Select value={stockSortBy} onValueChange={setStockSortBy}>
                    <SelectTrigger className="w-[130px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="stock-sort-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="reference" className="text-white">Référence</SelectItem>
                      <SelectItem value="designation" className="text-white">Désignation</SelectItem>
                      <SelectItem value="category" className="text-white">Catégorie</SelectItem>
                      <SelectItem value="available_quantity" className="text-white">Quantité</SelectItem>
                      <SelectItem value="nec" className="text-white">NEC Total</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="h-8 border-zinc-700 text-zinc-400 hover:text-white px-2"
                    onClick={() => setStockSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} data-testid="stock-sort-order">
                    {stockSortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                    <span className="ml-1 text-xs">{stockSortOrder === 'asc' ? 'A→Z' : 'Z→A'}</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleStockSort('reference')}>
                        <span className="flex items-center">Référence<StockSortIcon field="reference" /></span>
                      </TableHead>
                      <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleStockSort('designation')}>
                        <span className="flex items-center">Désignation<StockSortIcon field="designation" /></span>
                      </TableHead>
                      <TableHead className="text-zinc-400 cursor-pointer select-none hover:text-white" onClick={() => toggleStockSort('category')}>
                        <span className="flex items-center">Catégorie<StockSortIcon field="category" /></span>
                      </TableHead>
                      <TableHead className="text-zinc-400 text-right cursor-pointer select-none hover:text-white" onClick={() => toggleStockSort('available_quantity')}>
                        <span className="flex items-center justify-end">Disponible<StockSortIcon field="available_quantity" /></span>
                      </TableHead>
                      <TableHead className="text-zinc-400 text-right">Réservé</TableHead>
                      <TableHead className="text-zinc-400 text-right cursor-pointer select-none hover:text-white" onClick={() => toggleStockSort('nec')}>
                        <span className="flex items-center justify-end">NEC Total<StockSortIcon field="nec" /></span>
                      </TableHead>
                      <TableHead className="text-zinc-400">Dépôt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : filteredAndSortedStock.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Aucun stock enregistré</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredAndSortedStock.map((item, idx) => (
                        <TableRow key={idx} className="border-zinc-800 table-row-hover">
                          <TableCell className="font-mono text-cyan-400">
                            {item.product?.reference || item.product_id?.slice(0, 12)}
                          </TableCell>
                          <TableCell className="text-white max-w-[200px] truncate">
                            {item.product?.designation || '-'}
                          </TableCell>
                          <TableCell>
                            {item.product?.category && (
                              <Badge variant="outline" className={`badge-${item.product.category.toLowerCase()}`}>
                                {item.product.category}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-white">
                            {item.available_quantity || 0}
                          </TableCell>
                          <TableCell className="text-right font-mono text-zinc-400">
                            {item.reserved_quantity || 0}
                          </TableCell>
                          <TableCell className="text-right font-mono text-amber-400">
                            {((item.product?.nec || 0) * (item.available_quantity || 0) * 1000).toFixed(1)} g
                          </TableCell>
                          <TableCell className="text-zinc-400">
                            {item.depot_id?.slice(0, 8)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movements Tab */}
        <TabsContent value="movements" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-350px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Date</TableHead>
                      <TableHead className="text-zinc-400">Type</TableHead>
                      <TableHead className="text-zinc-400">Produit</TableHead>
                      <TableHead className="text-zinc-400 text-right">Quantité</TableHead>
                      <TableHead className="text-zinc-400">Lot</TableHead>
                      <TableHead className="text-zinc-400">Motif</TableHead>
                      <TableHead className="text-zinc-400">Utilisateur</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : movements.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                          <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Aucun mouvement enregistré</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      movements.map((mov) => (
                        <TableRow key={mov.id} className="border-zinc-800 table-row-hover">
                          <TableCell className="text-zinc-400">
                            {new Date(mov.created_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={getMovementBadgeClass(mov.movement_type)}>
                              <span className="flex items-center gap-1">
                                {getMovementIcon(mov.movement_type)}
                                {getMovementLabel(mov.movement_type)}
                              </span>
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-cyan-400">
                            {mov.product_id?.slice(0, 12)}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-bold ${
                            ['entry', 'return'].includes(mov.movement_type) ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {['entry', 'return'].includes(mov.movement_type) ? '+' : '-'}{mov.quantity}
                          </TableCell>
                          <TableCell className="font-mono text-zinc-500">
                            {mov.lot_number || '-'}
                          </TableCell>
                          <TableCell className="text-zinc-400 max-w-[150px] truncate">
                            {mov.reason || '-'}
                          </TableCell>
                          <TableCell className="text-zinc-400">
                            {mov.user_name || '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecast Tab */}
        <TabsContent value="forecast" className="mt-6">
          {/* Summary cards */}
          {forecastData && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-zinc-500 text-xs">En depot</p>
                  <p className="text-green-400 font-mono text-xl font-bold">{forecastData.total_in_depot}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-zinc-500 text-xs">En commande</p>
                  <p className="text-amber-400 font-mono text-xl font-bold">+{forecastData.total_on_order}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-zinc-500 text-xs">Previsionnel total</p>
                  <p className="text-cyan-400 font-mono text-xl font-bold">{forecastData.total_forecast}</p>
                </CardContent>
              </Card>
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-3 text-center">
                  <p className="text-zinc-500 text-xs">Commandes en cours</p>
                  <p className="text-white font-mono text-xl font-bold">{forecastData.pending_orders_count}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Search & Filter Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input value={forecastSearch} onChange={e => setForecastSearch(e.target.value)}
                placeholder="Rechercher par reference, designation..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
                data-testid="forecast-search" />
            </div>
            <select value={forecastFilter} onChange={e => setForecastFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
              <option value="all">Tous les produits</option>
              <option value="on_order_only">En commande uniquement</option>
              <option value="in_depot_only">En depot uniquement</option>
              <option value="both">En depot + en commande</option>
            </select>
            <span className="text-zinc-600 text-xs">Tri:</span>
            <select value={forecastSortBy} onChange={e => setForecastSortBy(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
              <option value="reference">Reference</option>
              <option value="designation">Designation</option>
              <option value="forecast">Previsionnel</option>
              <option value="on_order">En commande</option>
              <option value="in_depot">En depot</option>
            </select>
            <button onClick={() => setForecastSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300 hover:border-amber-500/50">
              {forecastSortOrder === 'asc' ? '↑ Crois.' : '↓ Decrois.'}
            </button>
          </div>

          {/* Forecast Table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-420px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400 text-xs">Reference</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Designation</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Cat.</TableHead>
                      <TableHead className="text-zinc-400 text-xs">ADR</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right text-green-400">En depot</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right text-amber-400">En commande</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right text-cyan-400 font-bold">Previsionnel</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Fournisseur(s)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!forecastData ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8">
                        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      </TableCell></TableRow>
                    ) : (() => {
                      let items = forecastData.items || [];
                      if (forecastSearch) {
                        const q = forecastSearch.toLowerCase();
                        items = items.filter(i => i.reference?.toLowerCase().includes(q) || i.designation?.toLowerCase().includes(q));
                      }
                      if (forecastFilter === 'on_order_only') items = items.filter(i => i.on_order > 0);
                      else if (forecastFilter === 'in_depot_only') items = items.filter(i => i.in_depot > 0 && i.on_order === 0);
                      else if (forecastFilter === 'both') items = items.filter(i => i.in_depot > 0 && i.on_order > 0);
                      items = [...items].sort((a, b) => {
                        let va = a[forecastSortBy] || '', vb = b[forecastSortBy] || '';
                        if (typeof va === 'string') va = va.toLowerCase();
                        if (typeof vb === 'string') vb = vb.toLowerCase();
                        if (va < vb) return forecastSortOrder === 'asc' ? -1 : 1;
                        if (va > vb) return forecastSortOrder === 'asc' ? 1 : -1;
                        return 0;
                      });
                      if (items.length === 0) return (
                        <TableRow><TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" /><p>Aucun produit</p>
                        </TableCell></TableRow>
                      );
                      return items.map((item) => (
                        <TableRow key={item.product_id} className="border-zinc-800/50">
                          <TableCell className="font-mono text-cyan-400 text-xs whitespace-nowrap">{item.reference}</TableCell>
                          <TableCell className="text-white text-xs max-w-[250px] truncate">{item.designation}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{item.category || '-'}</Badge></TableCell>
                          <TableCell>
                            {item.risk_class && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">{item.risk_class}</Badge>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-green-400">{item.in_depot > 0 ? item.in_depot : <span className="text-zinc-700">0</span>}</TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {item.on_order > 0 ? (
                              <span className="text-amber-400">+{item.on_order}</span>
                            ) : <span className="text-zinc-700">-</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-bold text-cyan-400">{item.forecast}</TableCell>
                          <TableCell className="text-xs text-zinc-500 max-w-[150px] truncate">
                            {item.orders?.length > 0 ? (
                              item.orders.map((o, i) => (
                                <span key={i} className="block">
                                  {o.supplier} ({o.quantity})
                                </span>
                              ))
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StockPage;
